import React, { createContext, useState, useEffect, useContext } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { listAlerts } from "./graphql/queries";
import { onCreateAlert } from "./graphql/subscriptions";
import { updateAlert } from "./graphql/mutations";
import { useUser } from "./UserContext";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { userRecord, userClass, activeProvider } = useUser();

  useEffect(() => {
    if (userRecord) {
      fetchNotifications();

      // Set up subscription for real-time notifications
      const subscription = API.graphql(
        graphqlOperation(onCreateAlert)
      ).subscribe({
        next: (data) => {
          const newNotification = data.value.data.onCreateAlert;
          if (newNotification) {
            // Check if this notification is for this user
            const shouldReceive = shouldReceiveNotification(newNotification);
            
            if (shouldReceive) {
              setNotifications(prev => [newNotification, ...prev]);
              setUnreadCount(prev => prev + 1);
              
              // Show browser notification if supported
              if (typeof Notification !== 'undefined' && Notification.permission === "granted") {
                new Notification(
                  newNotification.isAdminNotification ? "System Notification" : "New Notification", 
                  {
                    body: newNotification.title,
                    icon: "/logo192.png" // Assuming you have this icon
                  }
                );
              }
            }
          }
        },
        error: (error) => console.error("Subscription error:", error)
      });

      return () => subscription.unsubscribe();
    }
  }, [userRecord, activeProvider]);

  // Determine if the user should receive this notification
  const shouldReceiveNotification = (notification) => {
    // If no user record, don't show any notifications
    if (!userRecord) return false;
    
    // If it's an admin notification, everyone should receive it
    if (notification.isAdminNotification) return true;
    
    // If the notification is from the user's active provider
    if (activeProvider && notification.userID === activeProvider.id) return true;
    
    // If the user is the creator of the notification
    if (notification.userID === userRecord.id) return true;
    
    return false;
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const alertData = await API.graphql(graphqlOperation(listAlerts));
      let alerts = alertData.data.listAlerts.items;
      
      // Filter alerts based on the user's relationship with the provider
      alerts = alerts.filter(alert => shouldReceiveNotification(alert));
      
      // Sort by creation date, newest first
      const sortedAlerts = alerts.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setNotifications(sortedAlerts);
      
      // Calculate unread notifications
      const unread = sortedAlerts.filter(alert => !alert.read).length;
      setUnreadCount(unread);
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    // Update local state first for immediate UI feedback
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true } 
          : notification
      )
    );
    
    // Update unread count
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Update the read status in the database
    try {
      const input = {
        id: notificationId,
        read: true
      };
      
      await API.graphql(graphqlOperation(updateAlert, { input }));
    } catch (error) {
      console.error("Error updating notification read status:", error);
    }
  };

  const markAllAsRead = async () => {
    // Get all unread notifications
    const unreadNotifications = notifications.filter(notification => !notification.read);
    
    // Update local state
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
    
    // Reset unread count
    setUnreadCount(0);
    
    // Update all notifications in the database
    try {
      // Update each notification one by one
      const updatePromises = unreadNotifications.map(notification => {
        const input = {
          id: notification.id,
          read: true
        };
        return API.graphql(graphqlOperation(updateAlert, { input }));
      });
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error updating all notification read statuses:", error);
    }
  };

  // Request notification permission on component mount
  useEffect(() => {
    // Check if browser supports notifications
    if (typeof Notification !== 'undefined') {
      if (Notification.permission !== "granted" && Notification.permission !== "denied") {
        Notification.requestPermission();
      }
    }
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      markAsRead,
      markAllAsRead,
      refreshNotifications: fetchNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 